
import {
  defineComponent, onBeforeMount, reactive, watch, nextTick,
} from 'vue';
import message from 'ant-design-vue/lib/message';
import { Modal } from 'ant-design-vue';
import { useAppBase } from '@/core/composables/AppBase';
import {
  IInspecao, IInspecaoSecao1, IInspecaoSecao2, IInspecaoSecao3, IInspecaoSecao4, IInspecaoSecao5, IInspecaoSecao6, IInspecaoSecao7,
} from '@/models/Entidades/IInspecao';
import ComunicacaoApi from '@/core/components/UI/ComunicacaoApi.vue';
import ServicoInspecao from '@/servicos/ServicoInspecao';
import { EStatusResposta, IResposta } from '@/core/models/IResposta';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import InspecaoDadosPrincipais from '@/components/InspecaoDadosPrincipais.vue';
import InspecaoSecao1 from '@/components/InspecaoSecao1.vue';
import InspecaoSecao2 from '@/components/InspecaoSecao2.vue';
import InspecaoSecao3 from '@/components/InspecaoSecao3.vue';
import InspecaoSecao4 from '@/components/InspecaoSecao4.vue';
import InspecaoSecao5 from '@/components/InspecaoSecao5.vue';
import InspecaoSecao6 from '@/components/InspecaoSecao6.vue';
import InspecaoSecao7 from '@/components/InspecaoSecao7.vue';
import InspecaoArquivos from '@/components/InspecaoArquivos.vue';
import EditorHtml from '@/core/components/UI/EditorHtml.vue';
import CampoEmail from '@/core/components/UI/CampoEmail.vue';
import Icone from '@/core/components/UI/Icone.vue';
import { ICadastro } from '@/models/Entidades/ICadastro';
import { IUsuario } from '@/models/Entidades/IUsuario';
import { IDTORelatorioFotografico } from '@/models/DTO/IDTORelatorioFotografico';
import GerenciadorAutenticacao from '@/core/gerenciadores/GerenciadorAutenticacao';
import storeApp from '@/store/storeApp';
import { IDTOEnviarEmail } from '@/models/DTO/IDTOEnviarEmail';
import router from '@/router';

export default defineComponent({
  name: 'InspecaoCadastro',
  components: {
    ComunicacaoApi,
    InspecaoDadosPrincipais,
    InspecaoSecao1,
    InspecaoSecao2,
    InspecaoSecao3,
    InspecaoSecao4,
    InspecaoSecao5,
    InspecaoSecao6,
    InspecaoSecao7,
    InspecaoArquivos,
    EditorHtml,
    CampoEmail,
    Icone,
  },
  setup() {
    const {
      appBase, apresentarComunicacaoApi, apresentarResposta, apresentarMensagemSucesso, ocultarComunicacaoApi, obterCodigoRota, obterChaveRota,
    } = useAppBase();
    const gerenciadorAutenticacao = new GerenciadorAutenticacao();
    const servicoInspecao = new ServicoInspecao();

    const state = reactive({
      inspecao: {} as IInspecao,
      cep: '',
      apresentarCampos: false,
      apresentarMapa: false,
      apresentarCadastroPessoa: false,
      abaEnderecoLocalizacao: '1',
      bloquearCampos: false,
      gerarPdf: false,
      gerandoPdf: false,
      cnpjHistorico: '',
      consultandoCnpj: false,
      estruturaRelatorioFotografico: [] as IDTORelatorioFotografico[],
      acessoExterno: false,
      chaveAcesso: '',
      apresentarModalEmail: false,
      enviarEmail: {} as IDTOEnviarEmail,
      montandoEmail: false,
    });

    let debounce = 0;
    let monitorarAlteracoesInspecao = false;

    function abrirValores() {
      router.push({ name: 'InspecaoValoresCadastro', params: { codigo: state.inspecao.codigo } });
    }

    function editarInspecao() {
      state.bloquearCampos = false;
    }

    function marcarAlterado() {
      state.gerarPdf = false;
    }

    watch(
      () => state.inspecao,
      () => {
        if (!monitorarAlteracoesInspecao) {
          return;
        }

        marcarAlterado();
      },
      {
        deep: true,
        flush: 'post',
      },
    );

    async function executarSemMonitorarAlteracoes<T>(
      acao: () => Promise<T> | T,
    ): Promise<T> {
      const monitoramentoAnterior = monitorarAlteracoesInspecao;

      monitorarAlteracoesInspecao = false;

      try {
        return await acao();
      } finally {
        // Aguarda o Vue processar todas as alterações pendentes.
        await nextTick();

        monitorarAlteracoesInspecao = monitoramentoAnterior;
      }
    }

    async function obterInspecao(codigo: number) {
      appBase.carregando = true;

      try {
        await executarSemMonitorarAlteracoes(async () => {
          state.inspecao = await servicoInspecao.obter(codigo);
        });
      } finally {
        appBase.carregando = false;
      }

      state.bloquearCampos = true;
      clearTimeout(debounce);

      debounce = setTimeout(() => {
        state.gerarPdf = true;
      }, 600);
    }

    async function obterInspecaoPorCahve(chave: string) {
      appBase.carregando = true;

      try {
        const acesso = await servicoInspecao.obterInspecaoChave(chave);

        if (UtilitarioGeral.objetoValido(acesso)) {
          await executarSemMonitorarAlteracoes(async () => {
            state.inspecao = await servicoInspecao.obter(
              acesso.codigoInspecao,
            );
          });

          state.acessoExterno = true;
          state.gerarPdf = false;
        }
      } finally {
        appBase.carregando = false;
      }
    }

    async function limparDados() {
      state.acessoExterno = false;
      state.chaveAcesso = '';
      state.cnpjHistorico = '';
      state.consultandoCnpj = false;
      state.apresentarCampos = false;
      state.bloquearCampos = false;
      state.gerarPdf = false;
      state.gerandoPdf = false;
      state.apresentarCadastroPessoa = false;
      state.abaEnderecoLocalizacao = '1';
      state.inspecao = {} as IInspecao;
      state.inspecao.codigo = 0;
      state.inspecao.acessos = [];
      state.inspecao.cadastro = {} as ICadastro;
      state.inspecao.usuario = {} as IUsuario;
      state.inspecao.status = 1;
      // state.inspecao.relatorioFotografico = await servicoInspecao.estruturaRelatorioFotografico();
      state.inspecao.secao1 = {} as IInspecaoSecao1;
      state.inspecao.secao2 = {} as IInspecaoSecao2;
      state.inspecao.secao3 = {} as IInspecaoSecao3;
      state.inspecao.secao4 = {} as IInspecaoSecao4;
      state.inspecao.secao5 = {} as IInspecaoSecao5;
      state.inspecao.secao6 = {} as IInspecaoSecao6;
      state.inspecao.secao7 = {} as IInspecaoSecao7;
      state.inspecao.arquivos = [];
      state.cep = '';
      state.apresentarModalEmail = false;
      state.enviarEmail = {} as IDTOEnviarEmail;
      state.enviarEmail.titulo = 'Informações pendentes - Inspeção';
      state.montandoEmail = false;
      state.apresentarCampos = true;
    }

    onBeforeMount(async () => {
      await limparDados();
      const chaveInspecao = obterChaveRota();
      const codigoinspecao = obterCodigoRota();
      if (UtilitarioGeral.valorValido(chaveInspecao)) {
        // const parceiroIdentificado = storeApp.getters.obterParceiroPorUrl(window.location.origin);
        // if (UtilitarioGeral.valorValido(parceiroIdentificado.codigo)) {
        state.chaveAcesso = chaveInspecao;
        storeApp.mutations.atualizarApresentarLogoExterna(true);
        gerenciadorAutenticacao.salvarIdentificadorParceiro('1');
        document.documentElement.style.setProperty('--primary', '#38dca8');
        document.documentElement.style.setProperty('--primary-light', '#ecececd9');
        document.documentElement.style.setProperty('--primary-dark', '#2b3535');
        // } else {
        //  document.documentElement.style.setProperty('--primary', '#ad7e36');
        //  document.documentElement.style.setProperty('--primary-light', '#ad7e36');
        //   document.documentElement.style.setProperty('--primary-dark', '#fff');
        // }
        await obterInspecaoPorCahve(chaveInspecao);
      } else if (codigoinspecao > 0) {
        await obterInspecao(codigoinspecao);
      }

      await nextTick();
      monitorarAlteracoesInspecao = true;
    });

    async function montarEmail() {
      const conteudo = await servicoInspecao.montarEmail(state.inspecao);
      if (UtilitarioGeral.valorValido(conteudo)) {
        state.enviarEmail.corpo = conteudo;
        state.apresentarModalEmail = true;
      } else {
        message.warn('Marque quais perguntas deseja enviar.');
      }
    }

    async function confirmarEnvioEmail() {
      const retorno = await servicoInspecao.enviarEmail(state.enviarEmail);
      if (retorno.enviado) {
        apresentarMensagemSucesso('E-mail enviado com sucesso!');
        state.apresentarModalEmail = false;
      } else {
        message.warn(retorno.mensagem);
      }
    }

    async function salvar() {
      appBase.resposta = {} as IResposta;
      apresentarComunicacaoApi('Aguarde por favor... Estamos salvando as informações da inspeção.');
      if (state.inspecao.codigo === 0) {
        appBase.resposta = await servicoInspecao.incluir(state.inspecao);
      } else {
        appBase.resposta = await servicoInspecao.atualizar(state.inspecao, state.chaveAcesso);
      }
      if (appBase.resposta.status === EStatusResposta.Sucesso) {
        ocultarComunicacaoApi();
        if (state.inspecao.codigo === 0) {
          apresentarMensagemSucesso('Inspeção criada com sucesso!');
          await obterInspecao(appBase.resposta.codigo);
        } else {
          apresentarMensagemSucesso('Inspeção atualizada com sucesso!');
          if (!state.acessoExterno) {
            state.bloquearCampos = true;
          }
        }
        state.gerarPdf = true;
      } else {
        apresentarResposta();
      }
    }

    async function revisarInspecao() {
      appBase.resposta = {} as IResposta;
      apresentarComunicacaoApi('Aguarde por favor... Estamos criando a revisão da inspeção.');
      appBase.resposta = await servicoInspecao.revisar(state.inspecao.codigo);
      if (appBase.resposta.status === EStatusResposta.Sucesso) {
        ocultarComunicacaoApi();
        apresentarMensagemSucesso('Revisão da inspeção criada com sucesso!');
        await router.push({ name: 'InspecaoCadastroEditar', params: { codigo: appBase.resposta.codigo } });
        await obterInspecao(appBase.resposta.codigo);
        state.bloquearCampos = false;
      } else {
        apresentarResposta();
      }
    }

    function confirmarRevisaoInspecao() {
      Modal.confirm({
        title: 'Deseja criar uma Revisão desta Inspeção?',
        content: 'Será criada uma nova inspeção utilizando esta como base, mantendo as informações e fotos já cadastradas.',
        okText: 'Sim',
        cancelText: 'Não',
        autoFocusButton: null,
        onOk: async () => { await revisarInspecao(); },
      });
    }

    async function gerarPdf() {
      state.gerandoPdf = true;
      const retornoRelatorio = await servicoInspecao.gerarPdf(state.inspecao.codigo);
      state.gerandoPdf = false;
      if (retornoRelatorio.status === EStatusResposta.Sucesso) {
        UtilitarioGeral.downloadArquivo(retornoRelatorio.link);
      } else {
        message.warn(retornoRelatorio.mensagem);
      }
    }

    async function atualizarStatus(status:number) {
      state.inspecao.status = status;
      await salvar();
    }

    return {
      storeApp,
      appBase,
      state,
      salvar,
      UtilitarioData,
      servicoInspecao,
      atualizarStatus,
      gerarPdf,
      marcarAlterado,
      confirmarEnvioEmail,
      montarEmail,
      abrirValores,
      editarInspecao,
      confirmarRevisaoInspecao,
    };
  },
});
